"use client"

import { useMemo, useState, useTransition } from "react"
import { useRouter } from "next/navigation"
import { updateUserRolesAndBudgetAction } from "@/actions/roleActions"
import type { UserRoleRO } from "@ub/requisition-approval-client"
import { CheckCircleIcon, LoaderCircle } from "lucide-react"
import { toast } from "sonner"

import { getLinkWithPrefixFromURLAndLocale } from "@/lib/link-utils/link-utils"
import { Button } from "@/components/ui/components/button"
import { Checkbox } from "@/components/ui/components/checkbox"
import { Input } from "@/components/ui/components/input"
import { Label } from "@/components/ui/components/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/components/select"
import { aggregatePermissionsForRolesLocal } from "@/components/account/users/users-helper"
import type { UbAccountUserEditrolesViewStoryblok } from "@/components/storyblok/component-types"

import { FormRow } from "../shared/FormRow"

export function AccountUserEditRolesForm({
  customerNo,
  userId,
  allRoles,
  initiallyActiveRoleIds,
  initialOrderLimit,
  initialBudget,
  initialPeriod,
  blok,
  sbLocale,
}: {
  customerNo: string
  userId: string
  allRoles: UserRoleRO[]
  initiallyActiveRoleIds: string[]
  initialOrderLimit?: number
  initialBudget?: number
  initialPeriod?: string
  blok: UbAccountUserEditrolesViewStoryblok
  sbLocale: string
}) {
  const [selected, setSelected] = useState<string[]>(initiallyActiveRoleIds)
  const [saving, startTransition] = useTransition()
  // local state for spend limit, budget and period
  const [spendLimit, setSpendLimit] = useState<string>(
    initialOrderLimit && initialOrderLimit > 0 ? String(initialOrderLimit) : ""
  )
  const [budget, setBudget] = useState<string>(initialBudget && initialBudget > 0 ? String(initialBudget) : "")
  const allowedPeriods = new Set(["weekly", "monthly", "quarterly", "yearly"])
  const [period, setPeriod] = useState<string>(
    initialPeriod && allowedPeriods.has(initialPeriod) ? initialPeriod : "quarterly"
  )

  const permissions = useMemo(() => aggregatePermissionsForRolesLocal(allRoles, selected), [allRoles, selected])

  const router = useRouter()

  function toggleRole(id: string, fixed: boolean | undefined) {
    if (fixed) return
    setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]))
  }

  async function onSave() {
    startTransition(async () => {
      const orderLimitNum = spendLimit?.trim() === "" ? 0 : Number(spendLimit)
      const budgetLimitNum = budget?.trim() === "" ? 0 : Number(budget)
      const res = updateUserRolesAndBudgetAction({
        customerNo: customerNo,
        userId: userId,
        roleIds: selected,
        orderLimit: orderLimitNum,
        orderLimitCurrency: "EUR",
        budgetLimit: budgetLimitNum,
        budgetCurrency: "EUR",
        budgetPeriod: period,
      })

      if ((res as any)?.error) {
        toast.error(blok.label_text_failure ?? "")
      } else {
        toast.success(blok.label_text_success ?? "", {
          icon: <CheckCircleIcon className={"size-4 text-green-500"} />,
          description: "",
        })
        setTimeout(() => {
          router.replace(getLinkWithPrefixFromURLAndLocale(`/account/users/${encodeURIComponent(userId)}`, sbLocale))
        }, 1000)
      }
    })
  }

  return (
    <div className="flex flex-col gap-2 lg:gap-6">
      <FormRow label={blok.label_roles} htmlFor="roles">
        <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-300 bg-white p-4 hover:border-black sm:grid-cols-2">
          {allRoles.map((role) => (
            <div key={role.roleID} className="flex items-center space-x-2">
              <Checkbox
                id={role.roleID}
                checked={selected.includes(role.roleID ?? "")}
                onCheckedChange={() => role.roleID && toggleRole(role.roleID, role.fixed)}
                disabled={role.fixed}
              />
              <Label htmlFor={role.roleID} className="font-normal hover:cursor-pointer">
                {role.roleDisplayName ?? role.roleID}
              </Label>
            </div>
          ))}
        </div>
      </FormRow>

      <FormRow label={blok.label_permissions} htmlFor="permissions">
        <div className="space-y-1 pt-2 text-sm text-gray-700">
          {permissions.map((p) => (
            <p key={p.permissionID ?? p.permissionDisplayName}>{p.permissionDisplayName ?? p.permissionID}</p>
          ))}
        </div>
      </FormRow>

      <FormRow label={blok.label_limit_per_order} htmlFor="spend-limit">
        <div className="relative">
          <Input
            id="spend-limit"
            type="number"
            className="pl-12"
            placeholder={blok.label_unlimited ?? ""}
            value={spendLimit}
            onChange={(e) => setSpendLimit(e.target.value)}
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">EUR</span>
        </div>
      </FormRow>

      <FormRow label={blok.label_budget} htmlFor="budget">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Input
              id="budget"
              type="number"
              className="pl-12"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
            />
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-500">EUR</span>
          </div>
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">{blok.label_weekly}</SelectItem>
              <SelectItem value="monthly">{blok.label_monthly}</SelectItem>
              <SelectItem value="quarterly">{blok.label_quarterly}</SelectItem>
              <SelectItem value="yearly">{blok.label_yearly}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </FormRow>

      <div className="flex justify-end gap-4 pt-8">
        <Button
          type="reset"
          variant="outline"
          onClick={() =>
            router.replace(getLinkWithPrefixFromURLAndLocale(`/account/users/${encodeURIComponent(userId)}`, sbLocale))
          }
        >
          {blok.label_cancel}
        </Button>
        <Button type="button" onClick={onSave} disabled={saving}>
          {saving ? <LoaderCircle className={"animate-spin"} /> : blok.label_save_changes}
        </Button>
      </div>
    </div>
  )
}
